// Tree / Org-chart visual — parity port of insight-bi's insight-tree (its
// `insight_tree` + `insight_tree_orgchart` components), which renders with
// d3-org-chart (v3). It is built the way insight-bi builds it:
//
//   • one node per Hierarchy Level N value, its parent the Level N-1 value of
//     the same row; a row stops at its first empty level (insight-bi skips a
//     node with no id, and so every node under it);
//   • a level's FIRST column is its id — drawn small in the card's top-right
//     corner — and the level's other columns are centred beneath it, dark/large
//     and muted/small in turn;
//   • a node with children shows "Total Value: N", the first Values column
//     summed over the leaves under it, and the count of everyone beneath it;
//   • one top-level value IS the root. insight-bi has no answer for several
//     (d3-org-chart refuses a forest), so they are joined under a "Total" card.
//
// d3-org-chart is lazy-loaded so it never enters the main bundle.

import { useEffect, useRef, useState } from 'react'
import type { NativeRendererProps } from './nativeRenderers'
import type { VisualLayout, VisualOption } from './types'
import { mapVisualData, pickGroup, toNumber, type MappedColumn, type MappedData } from './dataUtils'
import { formatCell } from './formatters'

// insight-bi's card and spacing (d3-org-chart's defaults, which it keeps).
const NODE_WIDTH = 200
const NODE_HEIGHT = 120

// How many sibling cards to lay out per row when a node is expanded. Authorable
// via the Tree visual's `childrenPerRow` option (clamped 1..8); unset keeps
// d3-org-chart's own compact layout — two columns — which is what insight-bi
// draws.
function readChildrenPerRow(options: VisualOption[] | undefined): number | null {
  for (const o of options || []) {
    if (o && o.optionName === 'childrenPerRow') {
      const raw = (o.value ?? o.defaultValue) as unknown
      const n = Math.floor(Number(raw))
      if (Number.isFinite(n) && n >= 1) return Math.min(n, 8)
    }
  }
  return null
}

interface TreeNode {
  id: string
  parentId: string
  label: string
  // The level's id value (its first column) — the card's corner tag.
  code: string
  // The level's other columns in order; `hidden` ones still take their turn
  // in the dark/muted alternation, exactly as insight-bi's card does.
  fields: { text: string; hidden: boolean }[]
  codeHidden: boolean
  level: number
  // The first Values column summed over this node's rows (a leaf's own figure).
  amount: number
  values: Record<string, number>
  // Column-name → value for the level columns along this node's path, plus the
  // rolled-up measures. Emitted on node click for drill-through.
  record: Record<string, unknown>
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  )
}

// Each hierarchy level contributes one node level: `cols` is every column of
// the group in order, the first being the level's id. insight-bi keeps a column
// off the card when its HIDDEN is 'F' (the tree's own reading of the flag); a
// column hidden the platform's way (`true`) is kept off as well.
interface Level {
  cols: MappedColumn[]
}

function cardHiddenNames(visualLayout: VisualLayout | undefined): Set<string> {
  const out = new Set<string>()
  for (const g of (visualLayout?.ColumnGroups ?? []) as Array<{ COLUMNS?: Array<Record<string, unknown>> }>) {
    for (const c of g.COLUMNS ?? []) {
      if (String(c.HIDDEN ?? '').trim().toUpperCase() === 'F') out.add(String(c.NAME ?? c.name ?? ''))
    }
  }
  return out
}

function levelColumns(md: MappedData): Level[] {
  const levels: Level[] = []
  for (let i = 1; i <= 10; i += 1) {
    const cols = pickGroup(md, `hierarchylevel${i}`)
    if (cols.length) levels.push({ cols })
  }
  // Fall back to the generic dimension groups if no hierarchy levels mapped.
  if (!levels.length) {
    const dims = pickGroup(md, 'rows', 'columns').filter((c) => !c.hidden)
    for (const d of dims) levels.push({ cols: [d] })
  }
  return levels
}

const ROOT = '__root__'

function buildNodes(md: MappedData, visualLayout?: VisualLayout): { nodes: TreeNode[]; valueCols: MappedColumn[] } {
  const levels = levelColumns(md)
  const valueCols = pickGroup(md, 'values', 'measures').filter((c) => !c.hidden)
  const amountCol = valueCols[0]
  const offCard = cardHiddenNames(visualLayout)
  const isOff = (c: MappedColumn) => !!c.hidden || offCard.has(c.name)
  const byId = new Map<string, TreeNode>()
  const nodes: TreeNode[] = [{
    id: ROOT, parentId: '', label: 'Total', code: '', fields: [{ text: 'Total', hidden: false }],
    codeHidden: true, level: 0, amount: 0, values: {}, record: {},
  }]
  byId.set(ROOT, nodes[0])

  for (const row of md.rows) {
    const path: string[] = []
    const pathRecord: Record<string, unknown> = {}
    let parentId = ROOT
    for (let li = 0; li < levels.length; li += 1) {
      const cols = levels[li].cols
      const code = cellText(row, cols[0])
      if (!code) break
      path.push(code)
      // Carry every column of this level (incl. hidden id columns) into the
      // drill record so onSelect can emit a stable key, not just the label.
      for (const c of cols) pathRecord[c.name] = row[c.name]
      const id = path.join(' / ')
      let node = byId.get(id)
      if (!node) {
        const fields = cols.slice(1).map((c) => ({ text: cellText(row, c), hidden: isOff(c) }))
        const label = fields.find((f) => f.text && !f.hidden)?.text || code
        node = {
          id, parentId, label, code, fields, codeHidden: isOff(cols[0]), level: li + 1,
          amount: 0, values: {}, record: { ...pathRecord },
        }
        byId.set(id, node)
        nodes.push(node)
      }
      if (amountCol) node.amount += toNumber(row[amountCol.name])
      for (const vc of valueCols) {
        node.values[vc.name] = (node.values[vc.name] ?? 0) + toNumber(row[vc.name])
        node.record[vc.name] = node.values[vc.name]
      }
      parentId = id
    }
  }
  const tops = nodes.filter((n) => n.parentId === ROOT)
  if (tops.length === 1) {
    tops[0].parentId = ''
    return { nodes: nodes.slice(1), valueCols }
  }
  return { nodes, valueCols }
}

function cellText(row: Record<string, unknown>, c: MappedColumn): string {
  return formatCell(row[c.name], { type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern }).trim()
}

// insight-bi's `tot_amt`: a leaf counts its own figure, a parent the sum of its
// children's — its own row's figure is not added. Only parents carry one.
function totals(nodes: TreeNode[]): Map<string, number> {
  const kids = new Map<string, TreeNode[]>()
  for (const n of nodes) {
    if (!n.parentId) continue
    const list = kids.get(n.parentId)
    if (list) list.push(n)
    else kids.set(n.parentId, [n])
  }
  const out = new Map<string, number>()
  const walk = (n: TreeNode): number => {
    const children = kids.get(n.id)
    if (!children?.length) return n.amount
    let sum = 0
    for (const c of children) sum += walk(c)
    out.set(n.id, sum)
    return sum
  }
  for (const n of nodes) if (!n.parentId) walk(n)
  return out
}

// Border / shadow for a node card. The selected node keeps the same border but
// gets a soft elevated shadow (a gentle glow + lift) so it reads as "picked".
const CARD_BORDER = '1px solid #d3d3d391'
const CARD_SHADOW = '4px 4px #d3d3d31c'
const CARD_BORDER_SEL = CARD_BORDER
const CARD_SHADOW_SEL = '0 0 0 4px rgba(100,116,139,0.12), 0 8px 22px rgba(15,23,42,0.18)'

const PEOPLE_ICON =
  '<svg width="12" height="9" viewBox="0 0 24 18" style="padding-right:2px" aria-hidden="true"><circle cx="8" cy="5" r="4" fill="#716E7B"/><path d="M0 18c0-4.4 3.6-8 8-8s8 3.6 8 8z" fill="#716E7B"/><circle cx="17" cy="5" r="3.4" fill="#A8A5B0"/><path d="M13.5 10.6A7 7 0 0 1 24 16.5V18h-6.3c0-2.9-1.6-5.6-4.2-7.4z" fill="#A8A5B0"/></svg>'

// Colours: the page / host tokens, with insight-bi's light values as
// fallbacks, so a card follows a dark host (see surfaceTheme.ts).
// insight-bi's node card, piece for piece: the id tag in the top-right, the
// level's other fields centred from 40px down (dark 14px / muted 11px in
// turn), "Total Value" for a parent, and the everyone-beneath count at the foot.
function nodeContentHtml(node: TreeNode, total: number | undefined, subordinates: number, selected: boolean): string {
  let dark = true
  const lines: string[] = []
  for (const f of node.fields) {
    if (f.text && f.text === node.code) continue
    if (f.text) {
      lines.push(`<div style="color:${dark ? "var(--pg-text, var(--color-text, #2F2F2F))" : "var(--pg-muted, var(--color-text-secondary, #6E6E6E))"};font-size:${dark ? '14px' : '11px'};display:${f.hidden ? 'none' : 'flex'};justify-content:center;text-align:center;">${escapeHtml(f.text)}</div>`)
    }
    dark = !dark
  }
  const tag = node.code && !node.codeHidden
    ? `<div style="font-size:12px;color:#0a7575;top:5px;right:8px;position:absolute;">${escapeHtml(node.code)}</div>`
    : ''
  const totalLine = total != null && total !== 0
    ? `<div style="color:var(--pg-text, var(--color-text, #39383d));font-size:14px;padding-top:4px;display:flex;justify-content:center;">Total Value: ${escapeHtml(String(total))}</div>`
    : ''
  const foot = subordinates
    ? `<div style="display:flex;justify-content:center;align-items:center;">${PEOPLE_ICON}<div style="font-size:12px;color:var(--pg-muted, var(--color-text-secondary, #716E7B));margin-top:1px;">${subordinates}</div></div>`
    : ''
  return `
    <div data-node-card="1" data-node-id="${escapeHtml(node.id)}" style="background-color:var(--pg-surface, var(--color-bg-secondary, #fafafa));width:${NODE_WIDTH}px;height:${NODE_HEIGHT}px;box-sizing:border-box;border-radius:10px;border:${selected ? CARD_BORDER_SEL : CARD_BORDER};display:flex;flex-direction:column;gap:4px;overflow:hidden auto;box-shadow:${selected ? CARD_SHADOW_SEL : CARD_SHADOW};position:relative;font-family:inherit;">
      <div style="color:var(--pg-text, var(--color-text, #08011E));position:absolute;right:20px;top:17px;font-size:10px;">&middot;&middot;&middot;</div>
      ${tag}
      <div style="justify-content:center;margin-top:40px;">${lines.join('')}</div>
      ${totalLine}
      <div style="display:flex;padding:0 8px;height:30px;align-items:center;margin-top:auto;">${foot}</div>
    </div>`
}

// Re-apply the selected ring to whichever node card matches `selectedId`,
// clearing it from the rest. Called on click (immediate feedback) so we don't
// have to force a full chart re-render. The baked-in style in nodeContentHtml
// keeps the ring after expand/collapse re-renders.
function applyTreeSelection(container: HTMLElement | null, selectedId: string | null) {
  if (!container) return
  container.querySelectorAll<HTMLElement>('[data-node-card]').forEach((el) => {
    const on = selectedId != null && el.getAttribute('data-node-id') === selectedId
    el.style.border = on ? CARD_BORDER_SEL : CARD_BORDER
    el.style.boxShadow = on ? CARD_SHADOW_SEL : CARD_SHADOW
  })
}

// d3-org-chart's built-in "compact" mode packs a node's (collapsed) children
// into a fixed 2-column block. We re-bind its two compact-layout passes so the
// block uses `cols` columns instead — letting an expanded node show more than 2
// children per row when there's room — and route each child's connector
// straight to its parent (the stock 2-column central "bus" would run through the
// inner cards once there are >2 columns). chart is `any` because d3-org-chart
// ships no types for these internals; Vite resolves the un-minified source so
// the method names below are stable.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function installCompactColumns(chart: any, cols: number): void {
  const colCount = Math.max(1, Math.floor(cols))

  // Pass 1 — reserve a `colCount`-wide block (assigned to the first child) and
  // tag every child with its row/column. Mirrors the library's own
  // calculateCompactFlexDimensions but generalised past 2 columns.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chart.calculateCompactFlexDimensions = function (this: any, root: any) {
    const attrs = this.getChartState()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    root.eachBefore((node: any) => {
      node.firstCompact = null
      node.compactEven = null
      node.flexCompactDim = null
      node.firstCompactNode = null
      node.compactColumn = null
    })
    const dim = attrs.layoutBindings[attrs.layout].compactDimension
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    root.eachBefore((node: any) => {
      if (!(node.children && node.children.length > 1)) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const compactChildren = node.children.filter((d: any) => !d.children)
      if (compactChildren.length < 2) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      compactChildren.forEach((child: any, i: number) => {
        if (!i) child.firstCompact = true
        child.compactColumn = i % colCount
        child.compactEven = child.compactColumn < colCount / 2
        child.row = Math.floor(i / colCount)
      })
      let maxCol = 0
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const child of compactChildren) maxCol = Math.max(maxCol, dim.sizeColumn(child))
      const columnSize = maxCol * colCount
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowsMap = this.groupBy(compactChildren, (d: any) => d.row, (reduced: any[]) =>
        reduced.reduce((mx, d) => Math.max(mx, dim.sizeRow(d) + attrs.compactMarginBetween(d)), 0),
      )
      const rowSize = rowsMap.reduce((s: number, v: [string, number]) => s + v[1], 0)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      compactChildren.forEach((child: any) => {
        child.firstCompactNode = compactChildren[0]
        child.flexCompactDim = child.firstCompact
          ? [columnSize + attrs.compactMarginPair(child), rowSize - attrs.compactMarginBetween(child)]
          : [0, 0]
      })
      node.flexCompactDim = null
    })
  }

  // Pass 2 — spread each row's children evenly across the reserved block width
  // and stack the rows vertically.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chart.calculateCompactFlexPositions = function (this: any, root: any) {
    const attrs = this.getChartState()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    root.eachBefore((node: any) => {
      if (!node.children) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const compactChildren = node.children.filter((d: any) => d.flexCompactDim)
      const fch = compactChildren[0]
      if (!fch) return
      fch.x -= fch.flexCompactDim[0] / 2
      const leftEdge = fch.x
      const blockWidth = fch.flexCompactDim[0]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      compactChildren.forEach((child: any) => {
        child.x = leftEdge + (blockWidth * (child.compactColumn + 0.5)) / colCount
      })
      const centerX = leftEdge + blockWidth * 0.5
      const offsetX = node.x - centerX
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (Math.abs(offsetX) < 10) compactChildren.forEach((d: any) => (d.x += offsetX))
      const dim = attrs.layoutBindings[attrs.layout].compactDimension
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowsMap = this.groupBy(compactChildren, (d: any) => d.row, (reduced: any[]) =>
        reduced.reduce((mx, d) => Math.max(mx, dim.sizeRow(d)), 0),
      )
      const cum: number[] = []
      let acc = 0
      for (const [, h] of rowsMap as [string, number][]) {
        acc += h + attrs.compactMarginBetween(fch)
        cum.push(acc)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      compactChildren.forEach((child: any) => {
        child.y = child.row ? fch.y + cum[child.row - 1] : fch.y
      })
    })
  }

  // Route compact-child links straight to the parent (per-layout), so a row of
  // N cards fans out cleanly instead of converging on a 2-column centre bus.
  try {
    const lb = chart.layoutBindings()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const key of Object.keys(lb)) {
      const b = lb[key]
      b.linkCompactXStart = b.linkX
      b.linkCompactYStart = b.linkY
      b.compactLinkMidX = b.linkX
      b.compactLinkMidY = b.linkY
    }
    chart.layoutBindings(lb)
  } catch {
    /* link binding tweak is best-effort; layout still works without it */
  }
}

export function TreeOrgChart({ jsonData, visualLayout, optionsArr, onSelect }: NativeRendererProps) {
  const ref = useRef<HTMLDivElement>(null)
  const selectedIdRef = useRef<string | null>(null)
  // Live chart instance so the resize observer (below) can refit it without
  // rebuilding from data on every render.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartRef = useRef<any>(null)
  const [err, setErr] = useState<string | null>(null)
  const childrenPerRow = readChildrenPerRow(optionsArr ?? visualLayout?.options)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let chart: any = null
    const md = mapVisualData(jsonData, visualLayout)
    if (!md.rows.length) {
      setErr('No data.')
      return
    }
    const { nodes } = buildNodes(md, visualLayout)
    if (!nodes.length || (nodes.length === 1 && nodes[0].id === ROOT)) {
      setErr('No hierarchy columns to build a tree.')
      return
    }
    setErr(null)
    const byId = new Map(nodes.map((n) => [n.id, n]))

    import('d3-org-chart')
      .then(({ OrgChart }) => {
        if (cancelled || !ref.current) return
        const totalById = totals(nodes)
        chart = new OrgChart()
          .container(ref.current as HTMLElement)
          .data(nodes)
          .nodeWidth(() => NODE_WIDTH)
          .nodeHeight(() => NODE_HEIGHT)
          // Compact mode stacks a node's collapsed children into a grid — two
          // columns, as insight-bi draws it, unless `childrenPerRow` asks for
          // more (installCompactColumns below).
          .compact(true)
          // insight-bi's `.link { stroke:#c1b9c6; stroke-width:2px }`.
          .linkUpdate(function (this: SVGPathElement) {
            try {
              this.setAttribute('stroke', '#c1b9c6')
              this.setAttribute('stroke-width', '2')
            } catch {
              /* link styling is best-effort */
            }
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .nodeContent((d: any) => nodeContentHtml(d.data as TreeNode, totalById.get((d.data as TreeNode).id), Number(d.data?._totalSubordinates) || 0, (d.data as TreeNode).id === selectedIdRef.current))
        if (onSelect) {
          // onNodeClick passes the node id (v3) — look the node up and emit its record.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          chart.onNodeClick((arg: any) => {
            const id = typeof arg === 'string' ? arg : arg?.data?.id ?? arg?.id
            const node = byId.get(id)
            if (node && node.id !== ROOT) {
              selectedIdRef.current = node.id
              applyTreeSelection(ref.current, node.id)
              onSelect(node.record)
            }
          })
        }
        // Generalise compact packing to `childrenPerRow` columns before the
        // first layout pass — only when the author asked for it.
        if (childrenPerRow != null) installCompactColumns(chart, childrenPerRow)
        chart.render()
        chartRef.current = chart
        // Stop a plain two-finger / wheel scroll from zooming the tree (let the
        // page scroll), WITHOUT breaking pinch-zoom. On a trackpad the browser
        // delivers pinch as a `wheel` event with ctrlKey=true, and a normal
        // two-finger scroll as a `wheel` event with ctrlKey=false — so we reject
        // wheel only when ctrlKey is false. Touchscreen pinch fires touch events
        // (not wheel) and drag-to-pan fires mouse events, both still allowed.
        // d3-zoom reads its filter live per event, so swapping it post-render works.
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const zb = typeof (chart as any).zoomBehavior === 'function' ? (chart as any).zoomBehavior() : null
          if (zb && typeof zb.filter === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            zb.filter((event: any) => (event.type === 'wheel' ? event.ctrlKey : !event.button))
          }
        } catch {
          /* zoom filter is best-effort; ignore if the chart API shifts */
        }
      })
      .catch((e) => { if (!cancelled) setErr(`Failed to load tree chart: ${e instanceof Error ? e.message : String(e)}`) })

    return () => {
      cancelled = true
      chartRef.current = null
      try {
        if (ref.current) ref.current.innerHTML = ''
      } catch {
        /* ignore teardown errors */
      }
    }
  }, [jsonData, visualLayout, childrenPerRow])

  // Keep the tree sized to its container. d3-org-chart fixes the SVG width at
  // render time and has no built-in resize handling, so on a window / panel
  // resize the chart kept its old width and never reflowed. Observe the
  // container and, after it settles, push the new width into the chart and
  // refit so all nodes stay visible. rAF-throttled so a drag-resize coalesces
  // to one reflow per frame.
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    let raf = 0
    let lastW = el.clientWidth
    let lastH = el.clientHeight
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth
      const h = el.clientHeight
      // Width drives the tree layout; ignore pure height jitter (and 0 sizes
      // while the element is detached / hidden) to avoid needless reflows.
      if (w === 0 || (w === lastW && h === lastH)) return
      lastW = w; lastH = h
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const chart = chartRef.current
        if (!chart) return
        try {
          if (typeof chart.svgWidth === 'function') chart.svgWidth(w)
          if (typeof chart.svgHeight === 'function') chart.svgHeight(Math.max(h, 320))
          chart.render()
          if (typeof chart.fit === 'function') chart.fit()
        } catch {
          /* refit is best-effort; ignore if the chart API shifts */
        }
      })
    })
    ro.observe(el)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])

  if (err) return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, var(--color-text-secondary, #888))', textAlign: 'center' }}>{err}</div>
  return <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 320, overflow: 'hidden' }} />
}

export default TreeOrgChart
